<template>
  <div ref="rootContainer" class="relative h-screen min-h-screen bg-cover md:min-h-screen" :class="{ '!h-[100svh] !min-h-[100svh]': isSp }">
    <div class="absolute inset-0">
      <!-- 単一画像。SP/PC 出し分け・最適化・preload(LCP) はすべて OptimizedImage が担当 -->
      <OptimizedImage
        v-if="!hasMultipleImages"
        fetchpriority="high"
        :class="['h-full w-full object-cover outline-none']"
        :pc-image-url="firstSlide?.pc ?? ''"
        :pc-optimized-image-options="{ width: HERO_IMAGE_WIDTH_PC }"
        :sp-image-url="firstSlide?.sp ?? ''"
        :sp-optimized-image-options="{ width: HERO_IMAGE_WIDTH_SP }"
        preload
      />
      <!-- 複数画像。先頭スライド(LCP候補)を preload する -->
      <MainVisualSlider
        v-else
        class="h-full w-full object-cover outline-none"
        :pc-optimized-image-options="{ width: HERO_IMAGE_WIDTH_PC }"
        :slides="heroSlides"
        :sp-optimized-image-options="{ width: HERO_IMAGE_WIDTH_SP }"
        preload-first
      />
      <div class="absolute left-0 top-0 h-32 w-full"></div>
      <div class="absolute bottom-0 left-0 h-32 w-full"></div>
      <!--
        段階的ブラー背景：タイトルの少し上から徐々にブラーを強くする
        - backdrop-blur-[12px]: 最大12pxのブラーを適用
        - mask: 上から下にかけて透明度を段階的に変化させ、ブラーの強度を調整
          - 0%: 完全に透明（ブラーなし）
          - 8%刻み: 徐々に不透明度を上げることでブラー強度を段階的に増加
          - 80%: 完全な12pxブラーを適用
        - top: タイトル位置に応じて動的に調整される開始位置
      -->
      <div
        ref="blurOverlay"
        class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/20 via-black/10 to-transparent backdrop-blur-[12px]"
        :style="{
          top: blurStartPosition,
          mask: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.1) 8%, rgba(0,0,0,0.2) 16%, rgba(0,0,0,0.3) 24%, rgba(0,0,0,0.4) 32%, rgba(0,0,0,0.5) 40%, rgba(0,0,0,0.6) 48%, rgba(0,0,0,0.7) 56%, rgba(0,0,0,0.8) 64%, rgba(0,0,0,0.9) 72%, rgba(0,0,0,1) 80%)',
          WebkitMask:
            'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.1) 8%, rgba(0,0,0,0.2) 16%, rgba(0,0,0,0.3) 24%, rgba(0,0,0,0.4) 32%, rgba(0,0,0,0.5) 40%, rgba(0,0,0,0.6) 48%, rgba(0,0,0,0.7) 56%, rgba(0,0,0,0.8) 64%, rgba(0,0,0,0.9) 72%, rgba(0,0,0,1) 80%)',
        }"
      ></div>
      <!--
        黒色グラデーションオーバーレイ：ブラーと同じ位置から徐々に黒くする
        - transparent から black/50 (50%不透明度) まで段階的に変化
        - ブラーと組み合わせて自然な背景効果を作成
      -->
      <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent" :style="{ top: blurStartPosition }"></div>
      <!-- Text Content Area -->
      <div :class="['absolute bottom-12 left-4 right-4 md:left-[5%] md:right-[5%] e1:left-1/2 e1:right-auto e1:w-[90%] e1:max-w-[1136px] e1:-translate-x-1/2', textPositionBottomClass]">
        <div ref="textContentArea" :class="['relative z-10 px-0 pb-6 pt-10 text-left text-white md:px-0 md:pb-0', textContentWidthClass]">
          <span v-if="titleTag" class="mb-3 inline-block rounded px-2 py-0.5 text-xs leading-[1.4] text-white light-bg-accent">{{ titleTag }}</span>
          <h1 ref="titleElement" class="mb-0.5 break-words font-['Spectral'] font-normal tracking-[0.5px]" :class="titleClass">{{ titleEn }}</h1>
          <div v-if="summary" class="font-['Spectral'] text-[17px] font-normal leading-[24px] [&>ol]:space-y-2 [&>p]:mb-2 [&>ul]:space-y-2" v-html="summary"></div>
          <!-- タイトル下のカスタムコンテンツ用スロット -->
          <slot name="subtitle-content"></slot>
        </div>
      </div>
      <!-- Responsive Button Area（モバイルでは safe-area 対応で画面下端に収める） -->
      <div
        v-if="!hideMainVisualButton"
        class="absolute bottom-4 left-4 right-4 flex justify-center md:bottom-8 md:left-[5%] md:right-[5%] md:justify-end e1:left-1/2 e1:right-auto e1:w-[90%] e1:max-w-[1136px] e1:-translate-x-1/2"
        :class="{ 'pb-[env(safe-area-inset-bottom)]': isSp }"
      >
        <div class="w-full md:max-w-[368px]">
          <button
            class="h-12 w-full min-w-[120px] max-w-[368px] border border-white bg-transparent px-2 text-white transition-all duration-300 hover:bg-white hover:text-black"
            @click="handleButtonClick"
          >
            <span class="font-['Spectral'] text-base font-normal">{{ buttonLabel }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { buildResponsiveImageSlides } from '@/utils/wabunka/image/responsiveImageSlides';
import { OptimizedImage } from '@/wabunka-src/common/components';
import { MainVisualSlider } from '@/wabunka-src/common/features';

// ヒーローのフルスクリーン表示に対する変換後の横幅（retina 端末を含めて十分なサイズ）
const HERO_IMAGE_WIDTH_SP = 1080;
const HERO_IMAGE_WIDTH_PC = 1920;

interface Props {
  isSp: boolean;
  images: string[];
  pcImages?: string[];
  spImages?: string[];
  titleEn: string;
  summary?: string;
  onButtonClick?: () => void;
  buttonLabel?: string;
  /** タイトル上のタグ（旅程ページなどで日数に応じたラベルを表示） */
  titleTag?: string;
  /**
   * タイトル文字サイズのプリセット。
   * - default: 36px/40px（Destination など）
   * - compact: Mobile 28px/34px, Desktop 36px/40px
   * - itinerary: Mobile 20px/28px, Desktop 30px/36px（旅程ページ）
   */
  titleSizePreset?: 'default' | 'compact' | 'itinerary' | 'experience' | 'accommodation';
  /** メインビジュアルのボタンを非表示にする */
  hideMainVisualButton?: boolean;
}

const TITLE_SIZE_PRESETS: Record<NonNullable<Props['titleSizePreset']>, string> = {
  default: 'text-[36px] leading-[40px]',
  compact: 'text-[28px] leading-[34px] md:text-[36px] md:leading-[40px]',
  itinerary: 'text-[20px] leading-[28px] md:text-[30px] md:leading-[36px]',
  experience: 'text-[20px] leading-[28px] font-medium md:text-3xl md:font-normal',
  accommodation: 'text-[36px] leading-[40px] md:text-3xl',
};

const TEXT_CONTENT_CLASSES: Partial<Record<NonNullable<Props['titleSizePreset']>, string>> = {
  // accommodation は右側ボタンパネルがないため mr を除去し、848px に制限する
  accommodation: 'md:max-w-[848px]',
  // experience は右側オーバーレイがないため mr を除去し、848px に制限する
  experience: 'md:max-w-[848px]',
};

const TEXT_POSITION_BOTTOM_CLASSES: Partial<Record<NonNullable<Props['titleSizePreset']>, string>> = {
  // experience は固定CTAバー(66px)と重ならないよう余白32px + バー高さ66px = 98px を確保
  experience: 'md:bottom-[98px]',
};

const props = withDefaults(defineProps<Props>(), {
  isSp: false,
  titleSizePreset: 'default',
  pcImages: () => [],
  spImages: () => [],
  summary: '',
  onButtonClick: undefined,
  buttonLabel: 'Discover & Book',
  titleTag: '',
  hideMainVisualButton: false,
});

const titleClass = computed(() => TITLE_SIZE_PRESETS[props.titleSizePreset]);
const textPositionBottomClass = computed(() => {
  return TEXT_POSITION_BOTTOM_CLASSES[props.titleSizePreset] ?? 'md:bottom-8';
});
const textContentWidthClass = computed(() => {
  const preset = TEXT_CONTENT_CLASSES[props.titleSizePreset];
  if (preset) return preset;
  return props.hideMainVisualButton ? 'max-w-2xl md:max-w-none' : 'max-w-2xl md:max-w-none md:mr-[384px]';
});

const rootContainer = ref<HTMLElement>();
const titleElement = ref<HTMLElement>();
const blurStartPosition = ref('66.666667%');

// SP/PC を index で対にする（片方欠けはもう片方、いずれも無ければ images で補完）。
// 最適化・preload は描画側の OptimizedImage / MainVisualSlider が担当するため、ここは生URLの対を組むだけ。
const heroSlides = computed(() => buildResponsiveImageSlides(props.pcImages, props.spImages, props.images));
const hasMultipleImages = computed(() => heroSlides.value.length >= 2);
// 単一画像表示用の先頭スライド（生URL）
const firstSlide = computed(() => heroSlides.value[0]);

const MIN_BLUR_START_PERCENT = 20;
const MAX_BLUR_START_PERCENT = 95;

// ブラー開始位置を計算する関数（コンテナ基準で算出しモバイルのスクロール変動の影響を抑える）
const calculateBlurPosition = () => {
  if (!rootContainer.value || !titleElement.value) return;

  const titleRect = titleElement.value.getBoundingClientRect();
  const containerRect = rootContainer.value.getBoundingClientRect();
  const containerHeight = containerRect.height;

  // タイトルの上端から少し上（35px程度）をブラー開始位置にする。コンテナ基準で算出
  const titleTopFromContainer = titleRect.top - containerRect.top;
  const blurStartFromTop = Math.max(0, titleTopFromContainer - 35);
  const blurStartPercentage = (blurStartFromTop / containerHeight) * 100;

  // レイアウト未確定時（フォント・画像未読み込み等）の異常値を弾く
  if (blurStartPercentage < MIN_BLUR_START_PERCENT || blurStartPercentage > MAX_BLUR_START_PERCENT) {
    return;
  }

  blurStartPosition.value = `${blurStartPercentage}%`;
};

const runCalculateAfterLayoutReady = () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        calculateBlurPosition();
        // 初回はフォント読み込み後に再計算（フォント未読時は titleRect が不正確になり得る）
        document.fonts?.ready?.then(() => {
          calculateBlurPosition();
        });
      });
    });
  });
};

// リサイズやマウント時にブラー位置を再計算（モバイルではスクロール時の resize を無視して安定化）
let resizeTimer: ReturnType<typeof setTimeout> | null = null;
const handleResize = () => {
  if (resizeTimer) clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    calculateBlurPosition();
    resizeTimer = null;
  }, 150);
};

onMounted(() => {
  runCalculateAfterLayoutReady();
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  if (resizeTimer) clearTimeout(resizeTimer);
});

// タイトルが変わった時も再計算（レイアウトが変わるため）
watch(
  () => props.titleEn,
  () => {
    runCalculateAfterLayoutReady();
  },
);

const handleButtonClick = () => {
  props.onButtonClick?.();
};
</script>
